// ─────────────────────────────────────────────────────────────────
// outputEnforcer.js — Output Enforcement Layer
//
// Domain ownership: structure extraction + schema validation + repair.
// Does NOT decide why a mode exists. Does NOT contain policy thresholds.
// Reads schema from modeConfig.js. Validates and repairs LLM output.
//
// Pipeline position:
//   LLM Output → outputEnforcer → parser → engine
// ─────────────────────────────────────────────────────────────────
import { MODE_SCHEMAS, MODES, DEFAULT_MODE } from '../constants/modeConfig.js';

// ─────────────────────────────────────────────
// SECTION DETECTOR
// Identifies which content sections are present in LLM output.
// These are heuristic matches — not strict parsing.
// ─────────────────────────────────────────────
const SECTION_PATTERNS = {
  context:         /^(context|why|background|today|we.re|here.s why)/im,
  compression:     /\b\w[\w\s]+=\s*\w/m,                    // "X = Y" pattern
  framework:       /(if\s.+\s(→|->|then)|step \d|framework|rule:)/im,
  scenario:        /(user reports|technician|customer|scenario|imagine|situation)/im,
  checkpoint:      /(question|checkpoint|which|what is the best|what would you)/im,
  navigation:      /^\s*1\.\s*(continue|drill|review|compress|next)/im,
  mastery_signal:  /(you are (now|at|ready)|mastery (signal|check)|~?\d+%)/im,
  explanation:     /(because|this means|in other words|explained|let me explain)/im,
  teaching:        /(here.s (how|what)|let.s (look|cover|discuss)|fundamentally)/im,
  examples:        /(example:|for instance|such as|e\.g\.|consider)/im,
  lesson_context:  /(in this lesson|today we.re covering|the lesson)/im,
};

function detectSections(text) {
  const found = [];
  for (const [section, pattern] of Object.entries(SECTION_PATTERNS)) {
    if (pattern.test(text)) found.push(section);
  }
  return found;
}

// ─────────────────────────────────────────────
// COMPRESS MODE EXTRACTOR
// For COMPRESS mode, extract only "X = Y" lines.
// ─────────────────────────────────────────────
function extractCompressionOnly(text) {
  const lines = text.split('\n');
  const compressed = lines.filter(line => {
    const trimmed = line.trim();
    // Match "WORD = description" pattern (compression model format)
    return /^[A-Z][\w\s]+=\s*.+/.test(trimmed) ||
           /^\*{1,2}[\w\s]+\*{0,2}\s*=\s*.+/.test(trimmed);
  });

  if (compressed.length === 0) {
    // Fallback: try to extract any "X = Y" from the text
    const matches = text.match(/\b[\w\s]+=\s*[^=\n]{5,}/g) || [];
    return matches.map(m => m.trim()).join('\n');
  }

  return compressed.join('\n');
}

// ─────────────────────────────────────────────
// FORBIDDEN SECTION REMOVER
// Strips content that matches forbidden section patterns.
// Heuristic — removes paragraphs/sentences triggering the pattern.
// ─────────────────────────────────────────────
function removeForbiddenContent(text, forbidden) {
  if (!forbidden || forbidden.length === 0) return text;

  let result = text;

  for (const section of forbidden) {
    const pattern = SECTION_PATTERNS[section];
    if (!pattern) continue;

    // Split into paragraphs, remove those matching forbidden pattern
    const paragraphs = result.split(/\n\n+/);
    const filtered   = paragraphs.filter(p => !pattern.test(p));
    result = filtered.join('\n\n');
  }

  return result.trim();
}

// ─────────────────────────────────────────────
// REPAIR STRATEGIES
// ─────────────────────────────────────────────
const REPAIR_STRATEGIES = {

  COMPRESS: (text) => {
    // COMPRESS is strict: only mental models
    const extracted = extractCompressionOnly(text);
    return extracted || text; // fallback to original if extraction fails
  },

  DRILL: (text) => {
    // DRILL: remove explanations and teaching, keep scenario + question
    return removeForbiddenContent(text, ['explanation', 'teaching', 'lesson_context', 'mastery_signal', 'navigation']);
  },

  REVIEW: (text) => {
    // REVIEW: remove mastery signals and advanced content
    return removeForbiddenContent(text, ['mastery_signal', 'advanced_scenarios']);
  },

  LESSON: (text) => {
    // LESSON: generally permissive — minimal repair needed
    return text;
  },

  PROFILE: (text) => {
    // PROFILE: remove lesson content
    return removeForbiddenContent(text, ['lesson_content', 'checkpoints']);
  },
};

// ─────────────────────────────────────────────
// TYPED-BLOCK FAIL-CLOSE (Phase 2)
// Defense-in-depth for the delimited typed-lesson-block artifacts
// ([EXAMPLE_START]...[EXAMPLE_END]). Runs before mode repair. A WELL-FORMED
// block is left untouched so the downstream parser can extract it; only an
// UNCLOSED / truncated open delimiter (no matching close — the model ran out of
// tokens mid-artifact) is stripped to plain so a raw delimiter can never reach
// the learner. Never throws; on no match the text passes through unchanged.
function failCloseTypedBlocks(text) {
  let out = text;
  // Open delimiter present but no matching close anywhere after it → strip from
  // the open delimiter to end of text. Tolerates slashed close variants. The
  // DIAGRAM case matters most: a truncated <svg> must never reach the renderer.
  if (/\[EXAMPLE_START\]/.test(out) && !/\[\/?EXAMPLE_END\]/.test(out)) {
    out = out.replace(/\[EXAMPLE_START\][\s\S]*$/, '');
  }
  if (/\[DIAGRAM_START\]/.test(out) && !/\[\/?DIAGRAM_END\]/.test(out)) {
    out = out.replace(/\[DIAGRAM_START\][\s\S]*$/, '');
  }
  return out === text ? text : out.replace(/\n{3,}/g, '\n\n').trim();
}

// ─────────────────────────────────────────────
// MAIN ENFORCER
// Validates LLM output against mode schema.
// Repairs if violations found.
// Logs violations for Phase 2 observability layer.
// ─────────────────────────────────────────────
export function enforceOutput({ mode, llmOutput }) {
  if (!llmOutput) return { output: llmOutput, violations: [], repaired: false };

  // Fail-close malformed typed-block delimiters up front (well-formed blocks pass
  // through). Tracked separately so it never masks a clean pass-through as a mode
  // "repair" — but it does mean the output may differ from the raw LLM text.
  const sanitized      = failCloseTypedBlocks(llmOutput);
  const blocksRepaired = sanitized !== llmOutput;
  llmOutput            = sanitized;

  const schema = MODE_SCHEMAS[mode] || MODE_SCHEMAS[DEFAULT_MODE];

  // Detect what sections are present
  const presentSections = detectSections(llmOutput);

  // Find violations — sections present that are forbidden
  const violations = (schema.forbidden || []).filter(f =>
    presentSections.includes(f)
  );

  // If no mode violations, pass through (typed-block fail-close may still have
  // sanitized a malformed delimiter above — reflect that in `repaired`).
  if (violations.length === 0) {
    return { output: llmOutput, violations: [], repaired: blocksRepaired };
  }

  // Apply repair strategy for this mode
  const repairFn = REPAIR_STRATEGIES[mode] || REPAIR_STRATEGIES[MODES.LESSON];
  const repaired = repairFn(llmOutput);

  return {
    output:     repaired,
    violations,
    repaired:   true,
    mode,
  };
}

// ─────────────────────────────────────────────
// VIOLATION LOGGER (Phase 2 observability hook)
// Stub — replace with Supabase write in Phase 2.
// ─────────────────────────────────────────────
export function logViolations({ mode, violations, sessionId }) {
  if (!violations.length) return;
  // Phase 2: write to interaction_log with violation data
  console.warn(`[OutputEnforcer] Mode: ${mode} | Violations: ${violations.join(', ')} | Session: ${sessionId}`);
}
